import { ServiceCode, IntegrationSystem, SelectOption } from '@/shared/types';

import { LEGAL_AGE, SERVICE_CODES } from '@/lib/consts';

import { DocumentRegisteredDocDataFieldName, DocumentTypeEntity, DocumentTypeCode, UserService } from '@/api/types';

import { getERZDocumentTypeEntites } from '../../modules/erz/helpers';

const documentTypeCodeByServiceCode: Partial<Record<ServiceCode, DocumentTypeCode>> = {
  [SERVICE_CODES.COMMISSION_SALE]: DocumentTypeCode.COMMISSION_SALE,
  [SERVICE_CODES.PURCHASE]: DocumentTypeCode.PURCHASE,
  [SERVICE_CODES.TRANSPORT_ACROSS_BORDER]: DocumentTypeCode.TRANSPORT_ACROSS_BORDER,
  [SERVICE_CODES.COLD_STEEL_TRANSPORTATION]: DocumentTypeCode.COLD_STEEL_TRANSPORTATION,
  [SERVICE_CODES.STORAGE_CARRYING]: DocumentTypeCode.STORAGE_CARRYING,
  [SERVICE_CODES.COLD_STEEL_STORAGE_FOR_COLLECTION]: DocumentTypeCode.COLD_STEEL_STORAGE_FOR_COLLECTION,
  [SERVICE_CODES.RESIDENCE_RELOCATION]: DocumentTypeCode.RESIDENCE_RELOCATION,
  [SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION]: DocumentTypeCode.TEMPORARY_RESIDENCE_RELOCATION,
  [SERVICE_CODES.VALIDITY_PERIOD_PROLONGATION]: DocumentTypeCode.VALIDITY_PERIOD_PROLONGATION,
  [SERVICE_CODES.MISSING_PERSON]: DocumentTypeCode.MISSING_PERSON,
};

interface GetServiceDataReturnType<T extends ServiceCode> {
  service: {
    id: UserService['id'];
    name: UserService['name'];
    code: UserService['code'];
  };
  applicationAttachment: {
    id: DocumentTypeEntity['id'];
    fileName: DocumentTypeEntity['label'];
    typeDoc: DocumentTypeEntity['label'];
    code: DocumentTypeEntity<IntegrationSystem.ERZ>['code'];
    info: Record<string, never>;
    encoded_contents: [];
  };
}

export async function getServiceData<T extends ServiceCode>(
  serviceCode: T,
  currentService: UserService | null,
): Promise<GetServiceDataReturnType<T>> {
  const [documentEntities] = await getERZDocumentTypeEntites([
    documentTypeCodeByServiceCode[serviceCode] as DocumentTypeCode,
  ]).catch(() => []);

  return {
    service: {
      id: currentService?.id ?? '',
      name: currentService?.name ?? '',
      code: currentService?.code ?? serviceCode,
    },
    applicationAttachment: {
      id: documentEntities?.id || null,
      fileName: documentEntities?.label || null,
      typeDoc: documentEntities?.label || null,
      code: documentEntities?.code || null,
      info: {},
      encoded_contents: [],
    },
  };
}

export const checkIsCityWithSpecialStatus = (cityId: Nullable<string>): boolean => {
  const cityIdsWithSpecialStatus: string[] = [IdConstants.KYIV, IdConstants.SEVASTOPOL];

  return cityIdsWithSpecialStatus.includes(cityId ?? '');
};

export const maxFileSizeInMbDefault = 1.6 * 1024 * 1024; // 1.6 MB
export const legalAgeDate = new Date();
legalAgeDate.setFullYear(new Date().getFullYear() - LEGAL_AGE);

export type PayloadGroupMockup<T extends string = never> = Record<T, Record<string, Nullable<boolean> | []>>;

export enum FormConstants {
  INPUT_MAX_LENGTH = 120,
  INPUT_MAX_LENGTH_4 = 4,
  INPUT_MAX_LENGTH_5 = 5,
  INPUT_MAX_LENGTH_6 = 6,
  INPUT_MAX_LENGTH_7 = 7,
  INPUT_MAX_LENGTH_10 = 10,
  INPUT_MAX_LENGTH_20 = 20,
  INPUT_MAX_LENGTH_32 = 32,
  INPUT_MAX_LENGTH_50 = 50,
  INPUT_MAX_LENGTH_100 = 100,
  INPUT_MAX_LENGTH_200 = 200,
  INPUT_MAX_LENGTH_240 = 240,
  INPUT_MAX_LENGTH_250 = 250,
  INPUT_MAX_LENGTH_255 = 255,
  INPUT_MAX_LENGTH_500 = 500,
  INPUT_MAX_LENGTH_512 = 512,
  TEXTAREA_MAX_LENGTH = 3000,
}

export enum IdConstants {
  UKRAINE = '7974a75f-85b9-4f2c-9aeb-fb403cdd0a53',
  KYIV = '4c32c29c-4b71-4b3b-b481-52bf1e38f0fb',
  SEVASTOPOL = 'd487add9-b7ea-4350-9efe-1c36c2f2c461',
}

export enum DocumentLeaseAgreementDataFieldName {
  contractNumberRent = 'contractNumberRent',
  indexNumberRent = 'indexNumberRent',
  dateSignRent = 'dateSignRent',
  expirationDateRent = 'expirationDateRent',
  declaredDocRent = 'declaredDocRent',
}

export enum DocumentOwnershipCertificateDataFieldName {
  serialNumberDoc = 'serialNumberDoc',
  indexNumber = 'indexNumber',
  certificateDate = 'certificateDate',
  authDep = 'authDep',
  PropertyDeclaredDoc = 'PropertyDeclaredDoc',
}

export enum DocumentExractPrprDataFieldName {
  ownershipRegNumber = 'ownershipRegNumber',
  ownershipindexNumber = 'ownershipindexNumber',
  ownershipFormedBy = 'ownershipFormedBy',
  ownershipDate = 'ownershipDate',
  OwnershipDeclaredDoc = 'OwnershipDeclaredDoc',
}

export enum DocumentCertMarriageDataFieldName {
  marriageSeriaNumberDoc = 'marriageSeriaNumberDoc',
  marriageAuthDep = 'marriageAuthDep',
  marriageDate = 'marriageDate',
  marriageDeclaredDoc = 'marriageDeclaredDoc',
}

export enum DocumentCertIdpDataFieldName {
  numberVPO = 'numberVPO',
  dateIssueVPO = 'dateIssueVPO',
  VPODeclaredDoc = 'VPODeclaredDoc',
}

export enum DocumentLegalResidenceDataFieldName {
  numberExtract = 'numberExtract',
  authDepExtract = 'authDepExtract',
  addressDoc = 'addressDoc',
  ExtractDeclaredDoc = 'ExtractDeclaredDoc',
  dateExtract = 'dateExtract',
  dateExpired = 'dateExpired',
}

export enum PersonStatusFieldName {
  personAddDeclaredDoc = 'personAddDeclaredDoc',
  personStatusDocType = 'personStatusDocType',
  personStatusName = 'personStatusName',
  personStatusOrg = 'personStatusOrg',
  personStatusDocSerialNumber = 'personStatusDocSerialNumber',
  personStatusDocIssuingAuthority = 'personStatusDocIssuingAuthority',
  personStatusDocIssueDate = 'personStatusDocIssueDate',
  personStatusDocExpireDate = 'personStatusDocExpireDate',
  personStatusDocument = 'personStatusDocument',
}

export enum EmploymentDataFieldName {
  confirmEmploymentDocType = 'confirmEmploymentDocType',
  confirmEmploymentDocSerialNumber = 'confirmEmploymentDocSerialNumber',
  confirmEmploymentDocIssuingAuthority = 'confirmEmploymentDocIssuingAuthority',
  confirmEmploymentDocIssueDate = 'confirmEmploymentDocIssueDate',
  confirmEmploymentDocExpireDate = 'confirmEmploymentDocExpireDate',
  confirmEmploymentDocument = 'confirmEmploymentDocument',
}

export enum LearningDataFeildName {
  confirmLearningDoc = 'confirmLearningDoc',
  confirmLearningDocType = 'confirmLearningDocType',
  confirmLearningDocSerialNumber = 'confirmLearningDocSerialNumber',
  confirmLearningDocIssueDate = 'confirmLearningDocIssueDate',
  confirmLearningDocument = 'confirmLearningDocument',
}

export enum PaymentDataFieldName {
  paymentType = 'paymentType',
  paymentDocType = 'paymentDocType',
  paymentDocument = 'paymentDocument',
}

export enum AdditionalDocumentFieldName {
  additionalDoc = 'additionalDoc',
  additionalDocType = 'additionalDocType',
  additionalDocuments = 'additionalDocuments',
  additionalInfo = 'additionalInformation',
}

export enum HealthDataFieldName {
  healthDataDoc = 'healthDataDoc',
  healthDataDocType = 'healthDataDocType',
  healthDataDocSerialNumber = 'healthDataDocSerialNumber',
  healthDataDocIssuingAuthority = 'healthDataDocIssuingAuthority',
  healthDataDocIssueDate = 'healthDataDocIssueDate',
  healthDataDocExpireDate = 'healthDataDocExpireDate',
  healthDataDocument = 'healthDataDocument',
}

export enum InsuranceDataFieldName {
  confirmInsuranceDoc = 'confirmInsuranceDoc',
  confirmInsuranceActiveTabKey = 'confirmInsuranceActiveTabKey',
  confirmInsuranceDocuments = 'confirmInsuranceDocuments',
}

export enum TechnicalConditionDataFieldName {
  confirmTechnicalConditionDoc = 'confirmTechnicalConditionDoc',
  confirmTechnicalConditionDocType = 'confirmTechnicalConditionDocType',
  confirmTechnicalConditionDocSerialNumber = 'confirmTechnicalConditionDocSerialNumber',
  confirmTechnicalConditionDocIssuingAuthority = 'confirmTechnicalConditionDocIssuingAuthority',
  confirmTechnicalConditionDocIssueDate = 'confirmTechnicalConditionDocIssueDate',
  confirmTechnicalConditionDocument = 'confirmTechnicalConditionDocument',
}

export const attachmentMapping: {
  [key: string]: {
    info: {
      [key: string]: string;
    };
    encoded_contents?: string | unknown;
  };
} = {
  [DocumentRegisteredDocDataFieldName.registeredDocType]: {
    info: {
      serialNumberDoc: DocumentRegisteredDocDataFieldName.registeredLocalCommunityNumber,
      auth_dep: DocumentRegisteredDocDataFieldName.registeredLocalCommunityAuthDep,
      date: DocumentRegisteredDocDataFieldName.registeredLocalCommunityDate,
    },
    encoded_contents: DocumentRegisteredDocDataFieldName.registeredDocumentLocalCommunityDoc,
  },
  [DocumentTypeCode.LEASE_AGREEMENT]: {
    info: {
      serialNumberDoc: DocumentLeaseAgreementDataFieldName.contractNumberRent,
      indexNumber: DocumentLeaseAgreementDataFieldName.indexNumberRent,
      date: DocumentLeaseAgreementDataFieldName.dateSignRent,
      expirationDate: DocumentLeaseAgreementDataFieldName.expirationDateRent,
    },
    encoded_contents: DocumentLeaseAgreementDataFieldName.declaredDocRent,
  },
  [DocumentTypeCode.OWNERSHIP_CERTIFICATE]: {
    info: {
      serialNumberDoc: DocumentOwnershipCertificateDataFieldName.serialNumberDoc,
      indexNumber: DocumentOwnershipCertificateDataFieldName.indexNumber,
      date: DocumentOwnershipCertificateDataFieldName.certificateDate,
      auth_dep: DocumentOwnershipCertificateDataFieldName.authDep,
    },
    encoded_contents: DocumentOwnershipCertificateDataFieldName.PropertyDeclaredDoc,
  },
  [DocumentTypeCode.EXRACT_PRPR]: {
    info: {
      serialNumberDoc: DocumentExractPrprDataFieldName.ownershipRegNumber,
      indexNumber: DocumentExractPrprDataFieldName.ownershipindexNumber,
      date: DocumentExractPrprDataFieldName.ownershipDate,
      auth_dep: DocumentExractPrprDataFieldName.ownershipFormedBy,
    },
    encoded_contents: DocumentExractPrprDataFieldName.OwnershipDeclaredDoc,
  },
  [DocumentTypeCode.CERT_MARRIAGE]: {
    info: {
      serialNumberDoc: DocumentCertMarriageDataFieldName.marriageSeriaNumberDoc,
      date: DocumentCertMarriageDataFieldName.marriageDate,
      auth_dep: DocumentCertMarriageDataFieldName.marriageAuthDep,
    },
    encoded_contents: DocumentCertMarriageDataFieldName.marriageDeclaredDoc,
  },

  [DocumentTypeCode.CERT_IDP]: {
    info: {
      serialNumberDoc: DocumentCertIdpDataFieldName.numberVPO,
      date: DocumentCertIdpDataFieldName.dateIssueVPO,
    },
    encoded_contents: DocumentCertIdpDataFieldName.VPODeclaredDoc,
  },
  [DocumentTypeCode.LEGAL_RESIDENCE]: {
    info: {
      serialNumberDoc: DocumentLegalResidenceDataFieldName.numberExtract,
      date: DocumentLegalResidenceDataFieldName.dateExtract,
      expirationDate: DocumentLegalResidenceDataFieldName.dateExpired,
      auth_dep: DocumentLegalResidenceDataFieldName.authDepExtract,
    },
    encoded_contents: DocumentLegalResidenceDataFieldName.ExtractDeclaredDoc,
  },
  [TechnicalConditionDataFieldName.confirmTechnicalConditionDocType]: {
    info: {
      serialNumberDoc: TechnicalConditionDataFieldName.confirmTechnicalConditionDocSerialNumber,
      auth_dep: TechnicalConditionDataFieldName.confirmTechnicalConditionDocIssuingAuthority,
      date: TechnicalConditionDataFieldName.confirmTechnicalConditionDocIssueDate,
    },
    encoded_contents: TechnicalConditionDataFieldName.confirmTechnicalConditionDocument,
  },
  [PersonStatusFieldName.personStatusDocType]: {
    info: {
      org: PersonStatusFieldName.personStatusOrg,
      serialNumberDoc: PersonStatusFieldName.personStatusDocSerialNumber,
      auth_dep: PersonStatusFieldName.personStatusDocIssuingAuthority,
      date: PersonStatusFieldName.personStatusDocIssueDate,
    },
    encoded_contents: PersonStatusFieldName.personStatusDocument,
  },
  [LearningDataFeildName.confirmLearningDocType]: {
    info: {
      serialNumberDoc: LearningDataFeildName.confirmLearningDocSerialNumber,
      date: LearningDataFeildName.confirmLearningDocIssueDate,
    },
    encoded_contents: LearningDataFeildName.confirmLearningDocument,
  },
  [HealthDataFieldName.healthDataDocType]: {
    info: {
      serialNumberDoc: HealthDataFieldName.healthDataDocSerialNumber,
      date: HealthDataFieldName.healthDataDocIssueDate,
      expirationDate: HealthDataFieldName.healthDataDocExpireDate,
      auth_dep: HealthDataFieldName.healthDataDocIssuingAuthority,
    },
    encoded_contents: HealthDataFieldName.healthDataDocument,
  },
  [EmploymentDataFieldName.confirmEmploymentDocType]: {
    info: {
      serialNumberDoc: EmploymentDataFieldName.confirmEmploymentDocSerialNumber,
      date: EmploymentDataFieldName.confirmEmploymentDocIssueDate,
      auth_dep: EmploymentDataFieldName.confirmEmploymentDocIssuingAuthority,
      expirationDate: EmploymentDataFieldName.confirmEmploymentDocExpireDate,
    },
    encoded_contents: EmploymentDataFieldName.confirmEmploymentDocument,
  },
  [PaymentDataFieldName.paymentDocType]: {
    info: {},
    encoded_contents: PaymentDataFieldName.paymentDocument,
  },
  [AdditionalDocumentFieldName.additionalDocType]: {
    info: {},
    encoded_contents: AdditionalDocumentFieldName.additionalDocuments,
  },
  personPassportDocType: {
    info: {},
    encoded_contents: 'personPassportDocuments',
  },
  rnokppDocType: {
    info: {},
    encoded_contents: 'rnokppDoc',
  },
  photoCardDocType: {
    info: {},
    encoded_contents: 'photoCard',
  },
  missingPersonPhotoDocType: {
    info: {},
    encoded_contents: 'missingPersonPhoto',
  },
  disappearanceMissingPersonDocType: {
    info: {},
    encoded_contents: 'disappearanceMissingPersonDocument',
  },
  missingPersonAdditionalPersonalDocType: {
    info: {},
    encoded_contents: 'missingPersonAdditionalPersonalDocument',
  },
  extractErdrDocType: {
    info: {},
    encoded_contents: 'extractErdrDocument',
  },
  ancestralConnectionDocType: {
    info: {},
    encoded_contents: 'ancestralConnectionDocument',
  },
  leftPhotoDocType: {
    info: {},
    encoded_contents: 'weaponLeftPhoto',
  },
  rightPhotoDocType: {
    info: {},
    encoded_contents: 'weaponRightPhoto',
  },
  expertResearchDocType: {
    info: {
      auth_dep: 'expertOrganization',
      date: 'examinationDate',
    },
    encoded_contents: 'expertResearch',
  },
};

export const genders: SelectOption[] = [
  { id: 'U', label: 'Не визначена' },
  { id: 'M', label: 'Чоловіча' },
  { id: 'F', label: 'Жіноча' },
] as const;

export const genderOptions: SelectOption[] = [
  { id: 'M', label: 'Чоловіча' },
  { id: 'F', label: 'Жіноча' },
] as const;

export const vehicleTypes: SelectOption[] = [
  { id: '1', label: 'Автомобільний транспорт' },
  { id: '2', label: 'Залізничний транспорт' },
  { id: '3', label: 'Авіаційний транспорт' },
];

export const allowedForSelectionDocumentTypesForDeclaredPlaceOfResidence = [
  DocumentTypeCode.LEASE_AGREEMENT,
  DocumentTypeCode.OWNERSHIP_CERTIFICATE,
  DocumentTypeCode.LEGAL_RESIDENCE,
  DocumentTypeCode.CERT_IDP,
  DocumentTypeCode.CERT_MARRIAGE,
  DocumentTypeCode.EXRACT_PRPR,
];

export const YesNoSelectOptions = {
  YES: {
    id: '1',
    label: 'Так',
  },
  NO: {
    id: '0',
    label: 'Ні',
  },
} as const;

export const allowedToChooseAddressRegDocumentTypeCodes: DocumentTypeCode[] = [
  DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI,
];

export const allowedToChooseUkrainePassportDocumentTypeCodes: DocumentTypeCode[] = [
  DocumentTypeCode.PASSPORT,
  DocumentTypeCode.ID_CARD,
  DocumentTypeCode.INTERNATIONAL_PASSPORT,
];

export const allowedToChooseOthersPassportDocumentTypeCodes: DocumentTypeCode[] = [
  DocumentTypeCode.TEMPORARY_RESIDENCE,
  DocumentTypeCode.RESIDENCE,
  DocumentTypeCode.OLD_RESIDENCE,
];

export const allAllowedToChoosePassportDocumentTypeCodes: DocumentTypeCode[] = [
  ...allowedToChooseUkrainePassportDocumentTypeCodes,
  ...allowedToChooseOthersPassportDocumentTypeCodes,
];

export const allAllowedToChooseERZPassportDocumentTypeCodes: DocumentTypeCode[] =
  allAllowedToChoosePassportDocumentTypeCodes.filter((code) => code !== DocumentTypeCode.TEMPORARY_RESIDENCE);
